import { format } from 'svelte-i18n';
import { get } from 'svelte/store';

import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
import type { Device } from 'src/types/Device';
import { isCameraDevice } from 'src/helpers/interface_helpers';
import { getBatteryIcon, getBatteryIconStyle } from 'src/helpers/device_data_helpers';
import { hasCameraCloudStorageCached } from 'src/helpers/camera_cloud_storage_helpers';
import { invalidateSignedQuery } from 'src/services/cam-cloud/media-auth';
import { camera_storage_keys } from 'src/components/Camera/stores/camera_storage_keys';
import { camera_storage_resolved_keys } from 'src/components/Camera/stores/camera_storage_resolved_keys';
import { camera_storage_passphrases } from 'src/components/Camera/stores/camera_storage_passphrases';
import { camera_key_prompt } from 'src/components/Camera/stores/camera_key_prompt';
import { camera_setup_prompt } from 'src/components/Camera/stores/camera_setup_prompt';
import type { CameraRecording, CameraRecordingEntry, CameraRecordings } from '../types/camera-status';
import type { StorageStatus } from '../types/camera-storage';
import { getCameraMonitoringSettings } from './camera-monitoring-settings';

const _format = get(format);

/**
 * Build the `camera:{channel}` component key for a device.
 * Falls back to `camera:0` when the device has no channel set.
 */
export const getCameraComponentKey = (device?: Device | null): string => {
	return `camera:${device?.channel || 0}`;
};

/**
 * Whether motion detection is armed, from `status['camera:{n}'].arm`.
 *
 * @param settings - LEGACY_CAMERA_ARM_PRIVACY: fallback for firmware that still reports
 * `arm` in the camera config. Drop the parameter once no supported firmware does.
 */
export const isCameraArmed = (
	device: Device | null | undefined,
	status: DeviceStatusG2 | undefined,
	settings: DeviceSettingsG2 | undefined,
): boolean => {
	const cameraKey = getCameraComponentKey(device);
	return status?.[cameraKey]?.arm ?? settings?.[cameraKey]?.arm ?? false;
};

/**
 * Whether privacy mode is on, from `status['camera:{n}'].privacy`. While on, the camera
 * serves no stream, so callers use this to block the player.
 *
 * @param settings - LEGACY_CAMERA_ARM_PRIVACY: fallback for firmware that still reports
 * `privacy` in the camera config. Drop the parameter once no supported firmware does.
 */
export const isCameraPrivacyEnabled = (
	device: Device | null | undefined,
	status: DeviceStatusG2 | undefined,
	settings: DeviceSettingsG2 | undefined,
): boolean => {
	const cameraKey = getCameraComponentKey(device);
	return status?.[cameraKey]?.privacy ?? settings?.[cameraKey]?.privacy ?? false;
};

/**
 * Whether the camera has somewhere to put a recording: active local storage
 * (`storage:0.active`) or a Camera Cloud Storage slot. Without either, the device
 * can still report a `recordings` entry that never produces media, so the
 * recording UI is gated on this.
 */
export const isAnyStorageAvailable = (
	device: Device | null | undefined,
	status: DeviceStatusG2 | undefined,
): boolean => {
	const localStorage = (status as { 'storage:0'?: StorageStatus } | undefined)?.['storage:0'];
	return !!localStorage?.active || hasCameraCloudStorageCached(device?.id ?? '');
};

export const getCameraStatusLabelList = (
	device: Device,
	device_status: DeviceStatusG2,
	device_settings: DeviceSettingsG2,
) => {
	const cameraKey = getCameraComponentKey(device);
	let hasMotion = !!device_status?.[cameraKey]?.motion;
	let statusLabels: {
		name: string;
		icon: string;
		value: string;
		iconStyle?: string;
		iconColor?: string;
	}[] = [
		{
			name: 'motion',
			icon: hasMotion ? 'text-primary fa fa-person-walking' : 'fas fa-person',
			value: hasMotion
				? _format('motion', { default: 'Motion' })
				: _format('status_no', { default: 'No' }),
		},
	];

	const isRecording =
		isAnyStorageAvailable(device, device_status) &&
		!!getActiveRecording(device_status?.[cameraKey]?.recordings);
	if (isRecording) {
		statusLabels.push({
			name: 'recording',
			icon: 'fa-thin fa-circle-dot',
			iconStyle: 'font-size: 12px;',
			value: _format('camera_recording_short', { default: 'REC' }),
		});
	}

	const devicePower = device_status?.['devicepower:0'];
	if (typeof devicePower?.battery !== 'undefined') {
		const batteryPercent = devicePower?.battery?.percent;
		statusLabels.push({
			name: 'battery',
			icon: getBatteryIcon(batteryPercent),
			value:
				batteryPercent != null
					? batteryPercent + ' %'
					: _format('not_available_short', { default: 'N/A' }),
			iconStyle: getBatteryIconStyle(batteryPercent),
		});
	}

	return statusLabels;
};

export const getCameraDevices = (devices: Record<string, Device>): Device[] => {
	return Object.values(devices || {}).filter((device) => isCameraDevice(device)) as Device[];
};

export const hasCameraDevices = (devices: Record<string, Device>): boolean => {
	return getCameraDevices(devices).length > 0;
};

/**
 * Count for the Camera Monitoring dashboard card: configured cameras when set,
 * otherwise all camera devices on the account.
 */
export function getCameraMonitoringCount(homeData: unknown, devices: Record<string, Device>): number {
	const configuredCount = getCameraMonitoringSettings(homeData)?.cameras?.length ?? 0;
	if (configuredCount > 0) return configuredCount;
	return getCameraDevices(devices ?? {})?.length ?? 0;
}

/**
 * Clear all session-scoped camera state: the media signed query, encrypted key
 * backups, resolved master keys and the prompt memory. Call this on logout,
 * account switch, or home switch (see `cameraServicesUnload`). Latest recording
 * thumbnails need no clearing: they live in device status.
 *
 * @remarks
 * Dropping the key backups and resolved keys here is safe on an account switch:
 * backups are re-fetched from the active account's cloud (`loadCameraStorageKeys`)
 * and the plaintext `master_key` is re-derived on demand from the preserved
 * per-account passphrase. The account-scoped passphrases are intentionally NOT
 * cleared here so a returning account never has to re-enter its password; they are
 * only cleared on a real logout (see `handleLogout`).
 *
 * The prompt memory is reset because it is keyed by device only, so an account or
 * home that has never been prompted would otherwise inherit "already prompted".
 */
export function clearCameraStores(): void {
	console.log('[camera-helpers] Clearing all camera stores');
	invalidateSignedQuery();
	camera_storage_keys.clear();
	camera_storage_resolved_keys.clear();
	camera_key_prompt.reset();
	camera_setup_prompt.reset();
}

/**
 * Forget a user's preserved camera credentials. Call this only on a REAL logout
 * (not an account/profile switch): it drops the account-scoped recording
 * passphrases, so the next login must re-enter its password.
 *
 * @remarks
 * This is the counterpart to {@link clearCameraStores}: that soft clear is safe
 * on an account switch and deliberately keeps passphrases so a returning account
 * never has to re-enter its password, whereas this hard clear removes them. The
 * prompt memory is reset by both.
 *
 * @param email - Normalised account email to forget; when omitted, every
 * account's passphrases are cleared (used by the logout-all path).
 */
export function clearCameraCredentials(email?: string): void {
	if (email) {
		camera_storage_passphrases.removeAccount(email);
	} else {
		camera_storage_passphrases.clear();
	}
	camera_key_prompt.reset();
	camera_setup_prompt.reset();
}

export function isCameraOnline(cameraStatus: DeviceStatusG2): boolean {
	return cameraStatus?.online || false;
}

/**
 * Enter native fullscreen for a camera `<video>` element (incl. iOS WebKit fallbacks).
 */
export function requestVideoFullscreen(videoElement: HTMLVideoElement | null | undefined): boolean {
	if (!videoElement) return false;

	if (videoElement.requestFullscreen) {
		videoElement.requestFullscreen();
		return true;
	}

	const webkitVideo = videoElement as HTMLVideoElement & {
		webkitEnterFullscreen?: () => void;
		webkitRequestFullscreen?: () => void;
	};

	if (webkitVideo.webkitEnterFullscreen) {
		webkitVideo.webkitEnterFullscreen();
		return true;
	}

	if (webkitVideo.webkitRequestFullscreen) {
		webkitVideo.webkitRequestFullscreen();
		return true;
	}

	return false;
}

/**
 * Slack allowed on top of a recording's own duration before it counts as stale.
 * Covers device/browser clock skew and the delay of the status push that clears
 * the finished entry.
 */
const STALE_RECORDING_GRACE_S = 10;

/**
 * A recording the device should have finished but still reports. Entries without
 * a `duration` run until explicitly stopped, so they never go stale.
 */
const isRecordingStale = (rec: CameraRecordingEntry): boolean => {
	if (!rec.duration) return false;
	return Date.now() / 1000 > rec.ts + rec.duration + STALE_RECORDING_GRACE_S;
};

const findLatestRecording = (
	recordings: CameraRecordings | undefined,
	{ manualOnly }: { manualOnly: boolean },
): CameraRecording | null => {
	let latest: CameraRecording | null = null;

	for (const [id, rec] of Object.entries(recordings ?? {})) {
		const isEligible = rec.stream === 0 && !isRecordingStale(rec) && (!manualOnly || !rec.trigger);
		if (isEligible && (!latest || rec.ts > latest.ts)) {
			latest = { ...rec, id };
		}
	}

	return latest;
};

/**
 * Most recent in-progress recording on stream 0, however it was started.
 */
export function getActiveRecording(recordings: CameraRecordings | undefined): CameraRecording | null {
	return findLatestRecording(recordings, { manualOnly: false });
}

/**
 * Most recent in-progress recording on stream 0 that the user started via
 * `Camera.StartRecording`. Motion-triggered clips carry a `trigger` describing the
 * event that started them, which is what separates them from a manual recording;
 * the user neither started them nor should stop them, so they stay out of the
 * player overlay and the Record/Stop button.
 */
export function getManualRecording(recordings: CameraRecordings | undefined): CameraRecording | null {
	return findLatestRecording(recordings, { manualOnly: true });
}
